import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import axios from 'axios';

type UseSessionTimeoutArgs = {
    isAuthenticated: boolean | undefined;
    sessionTimeoutMinutes: number;
    sessionTimeoutWarningMinutes: number;
    logout: () => void;
};

// Owns the session-expiration/warning countdown and the "continue session" modal
// state, keeping that logic out of App's own render-time checks.
export const useSessionTimeout = ({
    isAuthenticated,
    sessionTimeoutMinutes,
    sessionTimeoutWarningMinutes,
    logout,
}: UseSessionTimeoutArgs) => {
    const [sessionExpiration, setSessionExpiration] = useState<DateTime>(DateTime.now().plus({ minutes: 2 }));
    const [sessionWarning, setSessionWarning] = useState<DateTime>(DateTime.now().plus({ minutes: 1 }));
    const [isTimeoutModalOpen, setIsTimeoutModalOpen] = useState(false);
    const [isWarned, setWarned] = useState(false);
    const [isComplete, setComplete] = useState(false);

    const resetSessionTimers = () => {
        setSessionExpiration(DateTime.now().plus({ minutes: sessionTimeoutMinutes }));
        setSessionWarning(DateTime.now().plus({ minutes: sessionTimeoutMinutes - sessionTimeoutWarningMinutes }));
    };

    useEffect(() => {
        resetSessionTimers();
    }, [sessionTimeoutMinutes, sessionTimeoutWarningMinutes]);

    // Ticks once a second so the DateTime checks below get re-evaluated while a session is active.
    const [, forceTick] = useState(0);
    useEffect(() => {
        if (!isAuthenticated || isComplete) {
            return;
        }

        const interval = setInterval(() => forceTick(tick => tick + 1), 1000);
        return () => clearInterval(interval);
    }, [isAuthenticated, isComplete]);

    if (isAuthenticated && isComplete === false && DateTime.now() > sessionExpiration) {
        setComplete(true);
        logout();
    }

    if (isAuthenticated && isComplete === false && isWarned === false && isTimeoutModalOpen === false && DateTime.now() > sessionWarning) {
        setWarned(true);
        setIsTimeoutModalOpen(true);
    }

    const continueSession = () => {
        setWarned(false);
        setIsTimeoutModalOpen(false);
        resetSessionTimers();
        void axios.post('/auth/extendsession', { headers: { "Content-Type": "application/json" } });
    };

    return {
        isTimeoutModalOpen,
        closeTimeoutModal: () => setIsTimeoutModalOpen(false),
        continueSession,
    };
};
